import { createClient, type SupabaseClient, type Session, type User } from '@supabase/supabase-js'

let supabaseClient: SupabaseClient | null = null
let configPromise: Promise<{ supabaseUrl: string; supabaseKey: string; stripePubKey: string; appUrl: string }> | null = null
let cachedAppUrl = ''

async function fetchConfig() {
  if (!configPromise) {
    configPromise = fetch('/api/config').then(res => res.json())
    configPromise.then(config => {
      if (config.appUrl) cachedAppUrl = config.appUrl
    }).catch(() => { /* ignore */ })
  }
  return configPromise
}

export function getAppUrl(): string {
  return cachedAppUrl || window.location.origin
}

export async function initSupabase(): Promise<SupabaseClient> {
  if (supabaseClient) return supabaseClient

  const config = await fetchConfig()

  if (!config.supabaseUrl || !config.supabaseKey) {
    throw new Error('Supabase configuration missing')
  }

  supabaseClient = createClient(config.supabaseUrl, config.supabaseKey)
  return supabaseClient
}

export function getSupabase(): SupabaseClient {
  if (!supabaseClient) {
    throw new Error('Supabase not initialized. Call initSupabase() first.')
  }
  return supabaseClient
}

// Alias for getSupabase that ensures initialization
export async function getSupabaseClient(): Promise<SupabaseClient> {
  if (!supabaseClient) {
    return initSupabase()
  }
  return supabaseClient
}

export async function getStripeKey(): Promise<string> {
  const config = await fetchConfig()
  return config.stripePubKey
}

// Auth helpers
export async function signInWithGoogle() {
  const supabase = getSupabase()
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: `${window.location.origin}/auth/callback`
    }
  })
  if (error) throw error
  return data
}

export async function login(email: string, password: string) {
  const supabase = getSupabase()
  const { data, error } = await supabase.auth.signInWithPassword({ email, password })
  if (error) throw error
  return data
}

export async function signup(email: string, password: string, metadata?: { fullName?: string }) {
  const supabase = getSupabase()
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        full_name: metadata?.fullName || ''
      }
    }
  })
  if (error) throw error
  return data
}

export async function logout() {
  const supabase = getSupabase()
  const { error } = await supabase.auth.signOut({ scope: 'global' })
  if (error) throw error
}

export async function getSession(): Promise<Session | null> {
  const supabase = getSupabase()
  const { data: { session } } = await supabase.auth.getSession()
  return session
}

export async function resetPassword(email: string) {
  const supabase = getSupabase()
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: window.location.origin + '/reset-password'
  })
  if (error) throw error
}

export async function updatePassword(newPassword: string) {
  const supabase = getSupabase()
  const { data, error } = await supabase.auth.updateUser({ password: newPassword })
  if (error) throw error
  return data
}

// API request helper with auth
export async function apiRequest<T = unknown>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  body?: unknown
): Promise<T> {
  const session = await getSession()
  if (!session) {
    throw new Error('Not authenticated')
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session.access_token}`
  }

  const options: RequestInit = { method, headers }
  if (body) {
    options.body = JSON.stringify(body)
  }

  const res = await fetch(endpoint, options)
  const data = await res.json()

  if (!res.ok) {
    throw new Error(data.error || 'API Error')
  }

  return data as T
}

// Helper to get user's first name
export function getUserFirstName(user: User | null): string {
  if (!user) return 'there'

  if (user.user_metadata?.full_name) {
    return user.user_metadata.full_name.split(' ')[0]
  }

  if (user.user_metadata?.name) {
    return user.user_metadata.name.split(' ')[0]
  }

  if (user.email) {
    return user.email.split('@')[0]
  }

  return 'there'
}
